/**
 * Home health demo — the Seattle roster (fictional).
 *
 * Shifts, PTO, blocks and per-diem declared days are REAL (availability rules in
 * the demo tenant). This fixture carries what the product does not model:
 * discipline grouping, employment type, traits, weekly points targets and
 * ceilings, the territories each clinician owns (the default assignment and where
 * their supply lands on the branch read), what they cover when the owner is full,
 * per-diem declared days beyond the horizon, last worked / last contact, ramp.
 * Emails are the binding key to the seeded users, and `homeZip` must agree with
 * the handle's entry in `seedAddresses.ts` — a clinician lives in a territory
 * they own (the per diems, who own none, derive their supply from this zip).
 *
 * Dates on a per-diem entry are calendar-day offsets from demo day; the store seed
 * resolves them to date keys. The cast's own anchors are working-day offsets, but a
 * per diem declares particular calendar days, weekends included.
 */
import type { RosterEntry } from "../state";
import { CEILING_POINTS_BY_EMPLOYMENT, TARGET_POINTS_BY_EMPLOYMENT, TRAIT_OASIS, TRAIT_WOUND_VAC } from "./config";

export const ROSTER_EMAIL_DOMAIN = "medarrive.com";
/** Every roster user's email is `<prefix><handle>@<domain>`; the reset uses it to recognize the seeded org. */
export const ROSTER_EMAIL_PREFIX = "home_health_demo.";
const email = (handle: string) => `${ROSTER_EMAIL_PREFIX}${handle}@${ROSTER_EMAIL_DOMAIN}`;

/**
 * Weekly hours targets and ceilings by employment type, kept for the KPI hours
 * engine; points are the planning unit (see `config.ts`).
 */
export const HOURS_TARGETS = {
  FT: { targetHours: 32, ceilingHours: 36 },
  PT: { targetHours: 14, ceilingHours: 16 },
  perDiem: { targetHours: 0, ceilingHours: 24 },
} as const;

export function pointsTargetsFor(employment: RosterEntry["employment"]): { targetPoints: number; ceilingPoints: number } {
  return { targetPoints: TARGET_POINTS_BY_EMPLOYMENT[employment], ceilingPoints: CEILING_POINTS_BY_EMPLOYMENT[employment] };
}

export interface RosterFixtureEntry extends Omit<RosterEntry, "territory" | "rampEndsOn" | "declaredDays" | "lastWorkedOn" | "lastContactOn"> {
  /** Per diem: days declared available, as working-day offsets from demo day (0 today, 1 the next working day). */
  declaredDayOffsets?: number[];
  lastWorkedOffset?: number;
  lastContactOffset?: number;
}

type RosterSeed = Omit<RosterFixtureEntry, "targetHours" | "ceilingHours" | "targetPoints" | "ceilingPoints"> &
  Partial<Pick<RosterEntry, "targetHours" | "ceilingHours" | "targetPoints" | "ceilingPoints">>;

const seed = (s: RosterSeed): RosterFixtureEntry => ({
  ...HOURS_TARGETS[s.employment],
  ...pointsTargetsFor(s.employment),
  ...s,
});

const ALL_TERRITORIES = ["North", "South", "East", "West"];

export const ROSTER_FIXTURE: RosterFixtureEntry[] = [
  seed({
    handle: "dana",
    gender: "F",
    firstName: "Dana",
    lastName: "Reyes",
    displayName: "Dana Reyes",
    email: email("dana"),
    discipline: "RN",
    roleKind: "assessing",
    employment: "FT",
    traits: [TRAIT_OASIS, TRAIT_WOUND_VAC],
    // North's owner lives in North: the seed and the product both price her day from home.
    homeZip: "98125",
    territories: ["North"],
  }),
  seed({
    handle: "marcus",
    gender: "M",
    firstName: "Marcus",
    lastName: "Tate",
    displayName: "Marcus Tate",
    email: email("marcus"),
    discipline: "RN",
    roleKind: "assessing",
    employment: "FT",
    traits: [TRAIT_OASIS, TRAIT_WOUND_VAC],
    homeZip: "98122",
    territories: ["East", "West"],
    coversTerritories: ["North"],
  }),
  seed({
    handle: "priya",
    gender: "F",
    firstName: "Priya",
    lastName: "Shah",
    displayName: "Priya Shah",
    email: email("priya"),
    discipline: "RN",
    roleKind: "assessing",
    employment: "FT",
    traits: [],
    rampWeek: 2,
    rampWeeks: 4,
    homeZip: "98126",
    territories: ["South"],
  }),
  seed({
    handle: "devon",
    gender: "M",
    firstName: "Devon",
    lastName: "Price",
    displayName: "Devon Price",
    email: email("devon"),
    discipline: "RN",
    roleKind: "assessing",
    employment: "perDiem",
    traits: [TRAIT_OASIS, TRAIT_WOUND_VAC],
    homeZip: "98199",
    territories: [],
    // Declared today, the call-out day two working days out — the coverage panel's
    // wound-VAC option (5.2) — and two more days across the next fortnight.
    declaredDayOffsets: [0, 2, 5, 7],
    lastWorkedOffset: -10,
    lastContactOffset: -2,
  }),
  seed({
    handle: "luis",
    gender: "M",
    languages: ["English", "Spanish"],
    firstName: "Luis",
    lastName: "Herrera",
    displayName: "Luis Herrera",
    email: email("luis"),
    discipline: "LPN",
    roleKind: "assistant",
    employment: "FT",
    traits: [],
    homeZip: "98109",
    // He owns West and *covers* North. Owning North too would put his 30 points on
    // the North skilled-nursing target as well as Dana's, doubling it to 60 against
    // demand of about 34 — which is why 5.2's "North SN over target" read false.
    // His North work still lands in the North cell, because demand follows the
    // patient's zip and `ownerFor` falls back to whoever covers the territory.
    territories: ["West"],
    coversTerritories: ["North"],
  }),
  seed({
    handle: "kim",
    gender: "F",
    firstName: "Kim",
    lastName: "Wong",
    displayName: "Kim Wong",
    email: email("kim"),
    discipline: "LPN",
    roleKind: "assistant",
    employment: "PT",
    traits: [],
    homeZip: "98144",
    territories: ["East", "South"],
  }),
  seed({
    handle: "ana",
    gender: "F",
    languages: ["English", "Spanish"],
    firstName: "Ana",
    lastName: "Garcia",
    displayName: "Ana Garcia",
    email: email("ana"),
    discipline: "PT",
    roleKind: "assessing",
    employment: "FT",
    traits: [TRAIT_OASIS],
    homeZip: "98106",
    territories: ALL_TERRITORIES,
  }),
  seed({
    handle: "sam",
    gender: "M",
    firstName: "Sam",
    lastName: "Ortiz",
    displayName: "Sam Ortiz",
    email: email("sam"),
    discipline: "PTA",
    roleKind: "assistant",
    employment: "PT",
    traits: [],
    homeZip: "98126",
    territories: ["South", "East"],
  }),
  seed({
    handle: "rachel",
    gender: "F",
    firstName: "Rachel",
    lastName: "Brooks",
    displayName: "Rachel Brooks",
    email: email("rachel"),
    discipline: "OT",
    roleKind: "assessing",
    employment: "FT",
    traits: [],
    homeZip: "98115",
    territories: ALL_TERRITORIES,
  }),
  seed({
    handle: "jordan",
    gender: "M",
    firstName: "Jordan",
    lastName: "Miles",
    displayName: "Jordan Miles",
    email: email("jordan"),
    discipline: "SLP",
    roleKind: "assessing",
    employment: "perDiem",
    traits: [TRAIT_OASIS],
    homeZip: "98108",
    ceilingHours: 16,
    territories: [],
    // Declared about once a week, including the working day before demo day.
    declaredDayOffsets: [-1, 4, 9],
    lastWorkedOffset: -1,
    lastContactOffset: -1,
  }),
  seed({
    handle: "elena",
    gender: "F",
    firstName: "Elena",
    lastName: "Vasquez",
    displayName: "Elena Vasquez",
    email: email("elena"),
    discipline: "MSW",
    roleKind: "msw",
    employment: "PT",
    traits: [],
    homeZip: "98119",
    territories: ALL_TERRITORIES,
  }),
  seed({
    handle: "tasha",
    gender: "F",
    firstName: "Tasha",
    lastName: "Lewis",
    displayName: "Tasha Lewis",
    email: email("tasha"),
    discipline: "HHA",
    roleKind: "assistant",
    employment: "FT",
    traits: [],
    homeZip: "98103",
    territories: ["North", "South"],
  }),

];
